
import { setHours, isAfter, isBefore, addHours, isToday, isValid, parse, format, startOfDay } from "date-fns";

// Constants
export const CUTOFF_HOUR = 14; // 2:00 PM
export const MIN_HOURS_BEFORE_PICKUP = 1; // Minimum hours before pickup

/**
 * Determines if a date should be disabled based on current time
 * If it's today and after cutoff hour (2 PM / 14:00), today should be disabled
 */
export const isDateDisabled = (date: Date, currentTime: Date = new Date()): boolean => {
  if (!isValid(date) || !isValid(currentTime)) return true;
  
  // If it's today and after the cutoff hour, today should be disabled
  if (isToday(date)) {
    // Create today's cutoff time using the current date
    const todayCutoff = setHours(startOfDay(currentTime), CUTOFF_HOUR);
    if (isAfter(currentTime, todayCutoff)) {
      return true;
    }
  }
  
  // If the date is before today, it should be disabled
  if (isBefore(startOfDay(date), startOfDay(currentTime))) {
    return true;
  }
  
  return false;
};

/**
 * Determines if a specific time slot should be disabled based on current time
 * Time slots should be disabled if they are in the past or less than minimum hours from now
 */
export const isTimeDisabled = (
  date: Date, 
  hour: number, 
  currentTime: Date = new Date(),
  minHoursBeforePickup: number = MIN_HOURS_BEFORE_PICKUP
): boolean => {
  if (!isValid(date) || hour < 0 || hour > 23) return true;
  
  // Create a date object with the specified hour
  const slotTime = setHours(startOfDay(date), hour);
  
  // Is this slot in the past?
  if (isBefore(slotTime, currentTime)) {
    return true;
  }
  
  // Is this slot less than minimum hours from now?
  const minTimeBeforePickup = addHours(currentTime, minHoursBeforePickup);
  if (isBefore(slotTime, minTimeBeforePickup)) {
    return true;
  }
  
  return false;
};

/**
 * Converts date string and hour string to a Date object
 */
export const getFormattedDateTime = (dateStr: string, hourStr: string): Date => {
  const date = new Date(dateStr);
  const hour = parseInt(hourStr, 10);
  return setHours(date, hour);
};

/**
 * Gets a summary of why a date or time is disabled
 */
export const getDisabledReason = (
  isDate: boolean, 
  date: Date, 
  hour?: number, 
  currentTime: Date = new Date()
): string => {
  if (isDate) {
    if (isToday(date)) {
      const todayCutoff = setHours(startOfDay(currentTime), CUTOFF_HOUR);
      if (isAfter(currentTime, todayCutoff)) {
        return `Bookings for today are closed after ${CUTOFF_HOUR}:00`;
      }
    }
    if (isBefore(startOfDay(date), startOfDay(currentTime))) {
      return "Cannot book for dates in the past";
    }
  } else if (hour !== undefined) {
    const slotTime = setHours(startOfDay(date), hour);
    
    if (isBefore(slotTime, currentTime)) {
      return "This time has already passed";
    }
    
    const minTimeBeforePickup = addHours(currentTime, MIN_HOURS_BEFORE_PICKUP);
    if (isBefore(slotTime, minTimeBeforePickup)) {
      return `Must be at least ${MIN_HOURS_BEFORE_PICKUP} hour from now`;
    }
  }
  
  return "Not available";
};

/**
 * Format a date for display - used for debugging and the time selector
 */
export const formatDateTimeForDisplay = (date: Date): string => {
  if (!isValid(date)) return "Invalid date";
  return format(date, "yyyy-MM-dd HH:mm:ss");
};
